import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import PublishIcon from '@mui/icons-material/Publish';
import ArchiveIcon from '@mui/icons-material/Archive';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CommentIcon from '@mui/icons-material/Comment';
import { useNavigate } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Plus } from '../../assets';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import { TableHeader } from '../../components/forms/TableHeader';
import {
  NewsClient,
  NewsItemDto,
  NewsItemStatus,
  getNewsItemStatus,
} from '../../app/services/api/news';

const newsClient = new NewsClient();

const NewsManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermissionTo, selectedFranchise } = useContext(UserContext);

  const canView = hasPermissionTo ? hasPermissionTo(['NewsManagementView', 'ConfigurationAdmin']) : true;
  const canCreate = hasPermissionTo ? hasPermissionTo(['NewsManagementCreate', 'ConfigurationAdmin']) : true;
  const canEdit = hasPermissionTo ? hasPermissionTo(['NewsManagementEdit', 'ConfigurationAdmin']) : true;
  const canDelete = hasPermissionTo ? hasPermissionTo(['NewsManagementDelete', 'ConfigurationAdmin']) : true;

  const [newsItems, setNewsItems] = useState<NewsItemDto[]>([]);

  const [searchText, setSearchText] = useState('');
  const [searchStatus, setSearchStatus] = useState<NewsItemStatus | ''>('');
  const [searchPinnedOnly, setSearchPinnedOnly] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [deleteTarget, setDeleteTarget] = useState<NewsItemDto | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!canView) return;
    loadData();
  }, [canView, selectedFranchise?.franchiseId]);

  const loadData = async () => {
    try {
      const data = await newsClient.getNewsItems({ includeExpired: true, franchiseId: selectedFranchise?.franchiseId });
      setNewsItems(data);
    } catch {
      setNewsItems([]);
    }
  };

  const filteredItems = useMemo(() => {
    return newsItems.filter(item => {
      const itemStatus = getNewsItemStatus(item.publishedAtUtc, item.expiresAtUtc);
      if (searchStatus && itemStatus !== searchStatus) return false;
      if (searchPinnedOnly && !item.isPinned) return false;
      if (searchText.trim()) {
        const text = searchText.toLowerCase();
        if (!item.title.toLowerCase().includes(text) &&
          !item.summary.toLowerCase().includes(text)) {
          return false;
        }
      }
      return true;
    });
  }, [newsItems, searchStatus, searchPinnedOnly, searchText]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pagedItems = useMemo(
    () => filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredItems, page, rowsPerPage],
  );

  const handleOpenCreate = () => {
    if (!canCreate) return;
    navigate('/news-management/new');
  };

  const handleOpenEdit = (item: NewsItemDto) => {
    if (!canEdit) return;
    navigate(`/news-management/${item.id}`);
  };

  const handleRequestDelete = (item: NewsItemDto, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canDelete) return;
    setDeleteTarget(item);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await newsClient.deleteNewsItem(deleteTarget.id);
      setNewsItems(prev => prev.filter(n => n.id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeleteConfirmOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteTarget(null);
    setDeleteConfirmOpen(false);
  };

  const handleTogglePin = async (item: NewsItemDto, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canEdit) return;
    try {
      const updated = await newsClient.updateNewsItem(item.id, {
        title: item.title,
        content: item.content,
        summary: item.summary,
        imageUrl: item.imageUrl,
        franchiseId: item.franchiseId,
        storeNumbers: item.storeNumbers,
        isGlobal: item.isGlobal,
        isPinned: !item.isPinned,
        publishedAtUtc: item.publishedAtUtc,
        visibleFromUtc: item.visibleFromUtc,
        expiresAtUtc: item.expiresAtUtc,
        tags: item.tags,
      });
      setNewsItems(prev => prev.map(n => n.id === updated.id ? updated : n));
    } catch {
      // Handle error
    }
  };

  const handlePublish = async (item: NewsItemDto, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canEdit) return;
    try {
      const updated = await newsClient.updateNewsItem(item.id, {
        title: item.title,
        content: item.content,
        summary: item.summary,
        imageUrl: item.imageUrl,
        franchiseId: item.franchiseId,
        storeNumbers: item.storeNumbers,
        isGlobal: item.isGlobal,
        isPinned: item.isPinned,
        publishedAtUtc: new Date().toISOString(),
        visibleFromUtc: item.visibleFromUtc,
        expiresAtUtc: item.expiresAtUtc,
        tags: item.tags,
      });
      setNewsItems(prev => prev.map(n => n.id === updated.id ? updated : n));
    } catch {
      // Handle error
    }
  };

  const handleArchive = async (item: NewsItemDto, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canEdit) return;
    try {
      const updated = await newsClient.updateNewsItem(item.id, {
        title: item.title,
        content: item.content,
        summary: item.summary,
        imageUrl: item.imageUrl,
        franchiseId: item.franchiseId,
        storeNumbers: item.storeNumbers,
        isGlobal: item.isGlobal,
        isPinned: item.isPinned,
        publishedAtUtc: item.publishedAtUtc,
        visibleFromUtc: item.visibleFromUtc,
        expiresAtUtc: new Date().toISOString(), // Set to now to expire/archive
        tags: item.tags,
      });
      setNewsItems(prev => prev.map(n => n.id === updated.id ? updated : n));
    } catch {
      // Handle error
    }
  };

  const getStatusColor = (status: NewsItemStatus) => {
    switch (status) {
      case 'Published':
        return 'success';
      case 'Draft':
        return 'warning';
      case 'Expired':
        return 'default';
      default:
        return 'default';
    }
  };

  const getScopeDisplay = (item: NewsItemDto) => {
    if (item.isGlobal) return 'All Stores';
    if (item.storeNumbers && item.storeNumbers.length > 0) {
      if (item.storeNumbers.length === 1) return `Store ${item.storeNumbers[0]}`;
      return `${item.storeNumbers.length} stores`;
    }
    return '-';
  };

  if (!canView) {
    return (
      <PageArea>
        <PageTitle>News Management</PageTitle>
        <Pill text='You do not have permission to view news items.' />
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}>
          <PageTitle subTitle="Create, publish and manage news items for your stores">
            News Management
          </PageTitle>
        </Grid>

        <Grid item
          xs={12}
          md={9}>
          <Grid container
            spacing={2}>
            <Grid item
              xs={12}
              md={4}>
              <TextField
                label='Search'
                fullWidth
                size='small'
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder='Search by title, summary or tags'
              />
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <TextField
                select
                label='Status'
                fullWidth
                size='small'
                value={searchStatus}
                onChange={e => setSearchStatus(e.target.value as NewsItemStatus | '')}
              >
                <MenuItem value=''>All Statuses</MenuItem>
                <MenuItem value='Draft'>Draft</MenuItem>
                <MenuItem value='Published'>Published</MenuItem>
                <MenuItem value='Archived'>Archived</MenuItem>
              </TextField>
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <TextField
                select
                label='Pinned'
                fullWidth
                size='small'
                value={searchPinnedOnly ? 'pinned' : 'all'}
                onChange={e => setSearchPinnedOnly(e.target.value === 'pinned')}
              >
                <MenuItem value='all'>All Items</MenuItem>
                <MenuItem value='pinned'>Pinned Only</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Grid>

        <Grid item
          xs={12}
          md={3}
          sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
          {canCreate && (
            <Button
              size='lg'
              variant='primary'
              startIcon={<Plus />}
              onClick={handleOpenCreate}
            >
              Create News Item
            </Button>
          )}
        </Grid>

        <Grid item
          xs={12}>
          <Section>
            <TableHeader>News Items</TableHeader>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell width={40}></TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Scope</TableCell>
                    <TableCell>Engagement</TableCell>
                    <TableCell align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedItems.map(row => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => handleOpenEdit(row)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Tooltip title={row.isPinned ? 'Unpin' : 'Pin'}>
                          <IconButton
                            size="small"
                            onClick={e => handleTogglePin(row, e)}
                            color={row.isPinned ? 'primary' : 'default'}
                          >
                            {row.isPinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="textSM"
                            fontWeight={500}>
                            {row.title}
                          </Typography>
                          <Typography variant="textXS"
                            color="text.secondary"
                            sx={{ display: 'block' }}>
                            {row.summary.length > 80 ? `${row.summary.substring(0, 80)}...` : row.summary}
                          </Typography>
                          {row.tags && row.tags.length > 0 && (
                            <Box sx={{ mt: 0.5 }}>
                              {row.tags.slice(0, 3).map(tag => (
                                <Chip
                                  key={tag}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  sx={{ mr: 0.5, height: 20, fontSize: '0.7rem' }}
                                />
                              ))}
                              {row.tags.length > 3 && (
                                <Chip
                                  label={`+${row.tags.length - 3}`}
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getNewsItemStatus(row.publishedAtUtc, row.expiresAtUtc)}
                          size="small"
                          color={getStatusColor(getNewsItemStatus(row.publishedAtUtc, row.expiresAtUtc))}
                        />
                      </TableCell>
                      <TableCell>{getScopeDisplay(row)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Tooltip title="Views">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <VisibilityIcon fontSize="small"
                                color="action" />
                              <Typography variant="textXS">{row.viewCount}</Typography>
                            </Box>
                          </Tooltip>
                          <Tooltip title="Likes">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <ThumbUpIcon fontSize="small"
                                color="action" />
                              <Typography variant="textXS">{row.likeCount}</Typography>
                            </Box>
                          </Tooltip>
                          <Tooltip title="Comments">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CommentIcon fontSize="small"
                                color="action" />
                              <Typography variant="textXS">{row.commentCount}</Typography>
                            </Box>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell align='right'
                        onClick={e => e.stopPropagation()}>
                        {getNewsItemStatus(row.publishedAtUtc, row.expiresAtUtc) === 'Draft' && canEdit && (
                          <Tooltip title="Publish">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={e => handlePublish(row, e)}
                            >
                              <PublishIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {getNewsItemStatus(row.publishedAtUtc, row.expiresAtUtc) === 'Published' && canEdit && (
                          <Tooltip title="Archive">
                            <IconButton
                              size="small"
                              onClick={e => handleArchive(row, e)}
                            >
                              <ArchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canEdit && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(row)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={e => handleRequestDelete(row, e)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {pagedItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8}
                        style={{ textAlign: 'center' }}>
                        No news items found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      count={filteredItems.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Section>
        </Grid>
      </Grid>

      {deleteTarget && (
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleCancelDelete}
        >
          <DialogTitle>Delete news item?</DialogTitle>
          <DialogContent>
            {`Are you sure you want to delete "${deleteTarget.title}"? This action cannot be undone.`}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete}
              disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              variant='primary'
              color='error'
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </PageArea>
  );
};

export default NewsManagementPage;
